// Iter 84 — Presentation restructure of the iter-58 behavioral pattern
// cards.  All analytics / metric calculations are UNCHANGED — we simply
// re-organise what each card shows:
//   • Pattern name + severity badge (Healthy / Moderate / Severe)
//   • Evidence:  "X trades"          (trigger_count from pattern_evidence)
//   • Impact:    "₹/$ figure"        (cost_usd from pattern_evidence)
//   • Action:    one short next-step recommendation
//   • Collapsible "ⓘ Research-backed" — citation / academic pattern name
//     is HIDDEN by default and only appears when the chip is expanded.
//
// Data still comes straight from `aggregates.behavioral` (and its
// `pattern_evidence` sub-tree).  No LLM round-trip.  No cross-user /
// percentile language anywhere — only the user's own numbers.
import React, { useState } from "react";
import {
  AlertTriangle,
  ShieldCheck,
  Clock,
  RefreshCw,
  Sparkles,
  Activity,
  Wallet,
  Info,
  ChevronDown,
} from "lucide-react";

// ─── Severity → token map ────────────────────────────────────────────────
// Iter 84 — relabel the three tiers to Healthy / Moderate / Severe.
// `good` / `warn` / `high` keys are kept so all existing builders keep
// working without changes.
const SEV = {
  good: {
    label: "Healthy",
    bg: "color-mix(in srgb, var(--bt-win) 10%, transparent)",
    border: "color-mix(in srgb, var(--bt-win) 35%, transparent)",
    text: "var(--bt-win)",
  },
  warn: {
    label: "Moderate",
    bg: "color-mix(in srgb, var(--bt-warn) 12%, transparent)",
    border: "color-mix(in srgb, var(--bt-warn) 38%, transparent)",
    text: "var(--bt-warn)",
  },
  high: {
    label: "Severe",
    bg: "color-mix(in srgb, var(--bt-loss) 12%, transparent)",
    border: "color-mix(in srgb, var(--bt-loss) 38%, transparent)",
    text: "var(--bt-loss)",
  },
};

// ─── Helper: format the $ impact figure as -$X,XXX (rupee figures are
// already converted to USD upstream so we render as $ uniformly).
function fmtImpact(usd) {
  if (usd === null || usd === undefined || Number.isNaN(usd)) return null;
  const n = Number(usd);
  if (!Number.isFinite(n) || n === 0) return null;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// ─── Card primitive ──────────────────────────────────────────────────────
function BiasCard({
  icon: Icon,
  title,
  severity,
  evidence,
  impact,
  action,
  namedPattern,
  citation,
  research,
  examples,
  testId,
  onExampleClick,
}) {
  const tone = SEV[severity] || SEV.warn;
  const [open, setOpen] = useState(false);
  const hasExamples = Array.isArray(examples) && examples.length > 0;
  const hasResearch = Boolean(namedPattern || citation || research || hasExamples);

  return (
    <article
      data-testid={testId}
      className="rounded-md border p-4 flex flex-col gap-3"
      style={{ background: tone.bg, borderColor: tone.border }}
    >
      {/* Header: icon + title + status badge */}
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            aria-hidden="true"
            className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
            style={{ background: "var(--bt-surface)", color: tone.text }}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
          </span>
          <h4 className="font-heading text-base leading-tight" style={{ color: "var(--bt-text)" }}>
            {title}
          </h4>
        </div>
        <span
          data-testid={`${testId}-severity`}
          className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm shrink-0"
          style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.border}` }}
        >
          {tone.label}
        </span>
      </header>

      {/* Evidence + Impact row */}
      <div className="grid grid-cols-2 gap-3">
        <div
          data-testid={`${testId}-evidence`}
          className="rounded-sm border p-2.5"
          style={{ background: "var(--bt-surface)", borderColor: "var(--bt-border)" }}
        >
          <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--bt-text-muted)" }}>
            Evidence
          </p>
          <p className="font-mono text-sm font-semibold mt-0.5" style={{ color: "var(--bt-text)" }}>
            {evidence || "—"}
          </p>
        </div>
        <div
          data-testid={`${testId}-impact`}
          className="rounded-sm border p-2.5"
          style={{ background: "var(--bt-surface)", borderColor: "var(--bt-border)" }}
        >
          <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--bt-text-muted)" }}>
            Impact
          </p>
          <p
            className="font-mono text-sm font-semibold mt-0.5"
            style={{ color: impact?.tone || "var(--bt-text)" }}
          >
            {impact?.value || "—"}
          </p>
        </div>
      </div>

      {/* Action */}
      {action && (
        <p
          data-testid={`${testId}-action`}
          className="text-sm leading-relaxed"
          style={{ color: "var(--bt-text)" }}
        >
          <span
            className="text-[10px] uppercase tracking-wider mr-1.5"
            style={{ color: tone.text }}
          >
            Action
          </span>
          {action}
        </p>
      )}

      {/* Research-backed collapsible */}
      {hasResearch && (
        <div className="pt-1 border-t" style={{ borderColor: tone.border }}>
          <button
            type="button"
            data-testid={`${testId}-research-toggle`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer hover:opacity-80 transition-opacity"
            style={{ color: "var(--bt-text-muted)" }}
          >
            <Info className="w-3.5 h-3.5" />
            <span>Research-backed</span>
            <ChevronDown
              className="w-3.5 h-3.5 transition-transform"
              style={{ transform: open ? "rotate(180deg)" : "none" }}
            />
          </button>
          {open && (
            <div
              data-testid={`${testId}-research-body`}
              className="mt-2 space-y-1.5"
            >
              {(namedPattern || citation) && (
                <p className="text-xs" style={{ color: "var(--bt-text-muted)" }}>
                  {namedPattern && (
                    <span className="font-semibold" style={{ color: "var(--bt-text)" }}>
                      {namedPattern}
                    </span>
                  )}
                  {namedPattern && citation && (
                    <span className="opacity-70"> · </span>
                  )}
                  {citation && <span className="opacity-90">{citation}</span>}
                </p>
              )}
              {research && (
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "var(--bt-text-muted)" }}
                >
                  {research}
                </p>
              )}
              {hasExamples && (
                <div
                  data-testid={`${testId}-examples`}
                  className="pt-1.5"
                >
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--bt-text-muted)" }}>
                    Top trades behind this pattern
                  </p>
                  <ul className="space-y-1">
                    {examples.slice(0, 3).map((ex, i) => {
                      const pnl = Number(ex?.pnl_usd ?? 0);
                      const pnlFmt =
                        Number.isFinite(pnl) && pnl !== 0
                          ? `${pnl < 0 ? "-" : "+"}$${Math.abs(pnl).toLocaleString("en-US", { maximumFractionDigits: 2 })}`
                          : "—";
                      const clickable = typeof onExampleClick === "function";
                      const rowProps = {
                        "data-testid": `${testId}-example-${i}`,
                        className: `flex items-center justify-between gap-2 font-mono text-xs rounded-sm px-2 py-1 ${
                          clickable
                            ? "cursor-pointer transition-colors hover:bg-[color-mix(in_srgb,var(--bt-accent)_12%,transparent)] focus:outline-none focus:ring-2 focus:ring-[var(--bt-accent)] w-full text-left"
                            : ""
                        }`,
                        style: {
                          background: "var(--bt-surface)",
                          border: "1px solid var(--bt-border)",
                        },
                      };
                      const inner = (
                        <>
                          <span className="flex items-center gap-2 min-w-0">
                            <span style={{ color: "var(--bt-text-muted)" }}>
                              {ex?.date || "—"}
                            </span>
                            <span className="truncate" style={{ color: "var(--bt-text)" }}>
                              {ex?.symbol || "Unknown"}
                            </span>
                            {ex?.side && (
                              <span
                                className="text-[10px] uppercase tracking-wider"
                                style={{
                                  color:
                                    ex.side === "long" || ex.side === "buy"
                                      ? "var(--bt-win)"
                                      : "var(--bt-loss)",
                                }}
                              >
                                {ex.side}
                              </span>
                            )}
                          </span>
                          <span
                            className="shrink-0 font-semibold"
                            style={{
                              color:
                                pnl < 0 ? "var(--bt-loss)" : pnl > 0 ? "var(--bt-win)" : "var(--bt-text-muted)",
                            }}
                          >
                            {pnlFmt}
                          </span>
                        </>
                      );
                      return clickable ? (
                        <li key={i}>
                          <button
                            type="button"
                            onClick={() => onExampleClick(ex)}
                            title="Jump to this trade in the table above"
                            {...rowProps}
                          >
                            {inner}
                          </button>
                        </li>
                      ) : (
                        <li key={i} {...rowProps}>
                          {inner}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Builders per metric group ───────────────────────────────────────────
// Each builder returns either a card config or null when nothing to show.
// Severity is computed from the iter-56 flags + thresholds (deterministic).
// `pe` (pattern_evidence sub-tree) supplies trigger_count + cost_usd to
// power the Evidence + Impact strips, when the metric has a matching
// pattern_evidence bucket.

function buildHoldTimeCard(b, HI, pe) {
  const h = b.hold_time || {};
  if (!h.sample_winners && !h.sample_losers) return null;
  const flagged = h.disposition_effect_flag === true;
  const ratio = h.loser_to_winner_ratio ?? 0;
  const disp = pe?.disposition_effect_losers || {};
  const triggerCount = disp.trigger_count ?? 0;
  const evidenceTotal = (h.sample_winners ?? 0) + (h.sample_losers ?? 0);
  const evidenceCount = flagged && triggerCount > 0 ? triggerCount : evidenceTotal;
  const impactUsd = fmtImpact(disp.cost_usd);

  return {
    testId: "bias-card-hold-time",
    icon: Clock,
    title: HI ? "Hold-time Imbalance" : "Hold-time Imbalance",
    severity: flagged ? "high" : "good",
    evidence: `${evidenceCount} ${evidenceCount === 1 ? "trade" : "trades"}`,
    impact:
      flagged && impactUsd
        ? { value: impactUsd, tone: "var(--bt-loss)" }
        : null,
    action: flagged
      ? (HI
          ? `Agar main losing trade hold karu (jaisa aapne ${h.avg_loser_minutes}m kiya jabki winners sirf ${h.avg_winner_minutes}m), to main entry ke saath ek hard ${h.avg_winner_minutes}m exit timer set karunga.`
          : `If I hold a losing trade (you held losers ${h.avg_loser_minutes}m vs winners ${h.avg_winner_minutes}m), then I will set a hard ${h.avg_winner_minutes}m exit timer at entry.`)
      : (HI
          ? `Agar main next trades execute karu (current ratio ${ratio}× healthy hai), to main yahi hold-time discipline maintain karunga.`
          : `If I execute the next set of trades (current ratio ${ratio}× is healthy), then I will maintain this same hold-time discipline.`),
    namedPattern: "Disposition Effect",
    citation: "Inspired by Odean 1998 · Shefrin & Statman 1985",
    research: HI
      ? `Odean (1998) ne paya tha ki traders 1.5× zyada likely hote hain winner sell karne ke liye. Loss Aversion (Kahneman & Tversky, 1979) ke according loss ka pain gain se 2–2.5× zyada feel hota hai — yahi reflex root cause hai. Note: yeh BrainTRADES check ek simplified proxy hai — hum losers ko ≥1.5× median winner hold-time tak hold karne wale trades count karte hain. Odean ka original PGR/PLR ratio methodology (Proportion of Gains Realized vs. Proportion of Losses Realized) alag hai — yeh version fast, sample-efficient, aur intraday journals ke liye tuned hai.`
      : `Odean (1998) found traders are 1.5× more likely to sell winners than losers. Root cause is Loss Aversion (Kahneman & Tversky 1979) — losses feel 2–2.5× more intense than equivalent gains. Note: this BrainTRADES check is a simplified proxy — we flag losers held ≥1.5× the median winner hold-time. Odean's original methodology uses the PGR/PLR ratio (Proportion of Gains Realized vs. Proportion of Losses Realized), which is different — this version is faster, sample-efficient, and tuned for intraday journals.`,
    examples: flagged ? disp.examples : null,
  };
}

function buildAfterLossCard(b, HI, pe) {
  const al = b.sequence?.after_loss || {};
  if ((al.sample ?? 0) === 0) return null;
  // Iter 99 — narrowed: this card now covers ONLY the rushed re-entry
  // + sizing-up behaviours (revenge $ drain).  The win-rate-collapse
  // flag gets its own dedicated card below (buildPostLossWrCollapse).
  const flagged = al.rushed_reentry_flag || al.size_jump_flag;
  // Combine the two related pattern_evidence buckets for after-loss issues.
  const revenge = pe?.revenge_trading || {};
  const rushed = pe?.rushed_reentry_after_loss || {};
  const triggerCount = Math.max(revenge.trigger_count ?? 0, rushed.trigger_count ?? 0);
  // Take whichever cost is more painful (the more negative one).
  const revengeCost = revenge.cost_usd ?? 0;
  const rushedCost = rushed.cost_usd ?? 0;
  const combinedCost = revengeCost < rushedCost ? revengeCost : rushedCost;
  const impactUsd = fmtImpact(combinedCost);
  const evidenceCount = flagged && triggerCount > 0 ? triggerCount : al.sample;

  // Iter 99 — sign reconciliation: when severe but $ is positive
  // (these trades net to a gain on raw P&L), the $ is misleading —
  // show the underlying behavioural metric that triggered the flag.
  let impact = null;
  if (flagged) {
    if (combinedCost >= 0) {
      // Positive (or zero) $ → behavioural metric instead.
      if (al.rushed_reentry_flag) {
        impact = {
          value: HI
            ? `Rushed re-entry · median gap ${al.median_gap_minutes}m`
            : `Rushed re-entry · median gap ${al.median_gap_minutes}m`,
          tone: "var(--bt-text)",
        };
      } else if (al.size_jump_flag) {
        impact = {
          value: HI
            ? `Size jump after loss · ${al.avg_position_size_vs_overall_ratio}\u00d7 avg`
            : `Size jump after loss · ${al.avg_position_size_vs_overall_ratio}\u00d7 avg`,
          tone: "var(--bt-text)",
        };
      }
    } else if (impactUsd) {
      impact = { value: impactUsd, tone: "var(--bt-loss)" };
    }
  }

  return {
    testId: "bias-card-after-loss",
    icon: RefreshCw,
    title: HI ? "After-loss Behaviour" : "After-loss Behaviour",
    severity: flagged ? "high" : "good",
    evidence: `${evidenceCount} ${evidenceCount === 1 ? "trade" : "trades"}`,
    impact,
    action: flagged
      ? (HI
          ? `Agar main koi trade loss mein close karu (after-loss WR ${al.win_rate_pct}%, median gap ${al.median_gap_minutes}m), to main next 15 minute tak naya trade nahi lunga aur agla setup likh ke hi enter karunga.`
          : `If I close a trade in loss (after-loss WR ${al.win_rate_pct}%, median gap ${al.median_gap_minutes}m), then I will not take a new trade for the next 15 minutes and only enter after writing down the next setup.`)
      : (HI
          ? `Agar main loss face karu, to main composed rahunga aur same position-sizing rules follow karunga — yeh recovery discipline strong hai.`
          : `If I take a loss, then I will stay composed and follow the same position-sizing rules — this recovery discipline is already strong.`),
    namedPattern: "Revenge Trading",
    citation: "Lo & Repin 2002",
    research: HI
      ? `"Win it back" reflex har trader ko hota hai. Loss ke baad cortisol aur dopamine seek-mode mein chale jaate hain — yeh universal pattern hai jiska sirf ek cure hai: structural cool-off rule.`
      : `The "win it back" reflex is universal. After a loss, cortisol and dopamine push the brain into seek-mode — the only reliable cure is a structural cool-off rule, not willpower.`,
    // Cite trades from whichever after-loss bucket has more evidence.
    examples: flagged
      ? ((revenge.examples?.length || 0) >= (rushed.examples?.length || 0)
          ? revenge.examples
          : rushed.examples)
      : null,
  };
}


// Iter 99 — Dedicated Post-Loss Win Rate Collapse card.  Different
// basis from After-loss Behaviour ($ drain) — measures the % win-rate
// drop after a loss (a discipline / focus indicator).  Mirrors the
// PDF's iter-96/97/98/99 logic so the WEB and PDF stay in sync.
function buildPostLossWrCollapseCard(b, HI, pe) {
  const al = b.sequence?.after_loss || {};
  if ((al.sample ?? 0) === 0) return null;
  if (!al.win_rate_drop_flag) {
    // Healthy card — render as "Healthy" so the trader knows we
    // checked this dimension.  Skip rendering only when the upstream
    // sample is empty (already handled above).
    return {
      testId: "bias-card-post-loss-wr",
      icon: RefreshCw,
      title: HI ? "Post-Loss Win Rate Collapse" : "Post-Loss Win Rate Collapse",
      severity: "good",
      evidence: `${al.sample} ${al.sample === 1 ? "trade" : "trades"}`,
      impact: null,
      action: HI
        ? `Agar main loss face karu, to main same WR maintain karta hoon — yeh focus discipline already strong hai.`
        : `If I take a loss, I maintain the same WR — focus discipline is already strong here.`,
      namedPattern: "Win-rate Collapse After Loss",
      citation: "Coval & Shumway 2005",
      research: HI
        ? `Loss ke baad WR girne ka pattern — sirf strategy nahi, focus aur emotional regulation reflect karta hai.`
        : `A drop in WR after losses reflects focus / emotional regulation, not just strategy quality.`,
      examples: null,
    };
  }

  const postLoss = pe?.post_loss_state_trades || {};
  const overall = b.sequence?.overall_win_rate_pct;
  const cost = postLoss.cost_usd;
  const evidenceCount = postLoss.trigger_count ?? al.sample;

  // Sign reconciliation: prefer the WR-drop metric whenever the $ is
  // non-negative (or absent) — the value of this card is the
  // discipline signal, not the dollar.
  let impact;
  if (cost !== undefined && cost < 0) {
    impact = { value: fmtImpact(cost), tone: "var(--bt-loss)" };
  } else if (overall !== undefined && al.win_rate_pct !== undefined) {
    impact = {
      value: `WR drop · ${overall}% \u2192 ${al.win_rate_pct}% across ${evidenceCount} post-loss trades`,
      tone: "var(--bt-text)",
    };
  } else {
    impact = null;
  }

  return {
    testId: "bias-card-post-loss-wr",
    icon: RefreshCw,
    title: HI ? "Post-Loss Win Rate Collapse" : "Post-Loss Win Rate Collapse",
    severity: "high",
    evidence: `${evidenceCount} ${evidenceCount === 1 ? "trade" : "trades"}`,
    impact,
    action: HI
      ? `Agar main loss face karu (aapki WR ${overall}% se gir ke ${al.win_rate_pct}% ho jaati hai), to main 15 minute ka structural cool-off lunga aur agla setup likh ke hi enter karunga.`
      : `If I take a loss (WR drops from ${overall}% to ${al.win_rate_pct}%), then I will take a structural 15-minute cool-off and only enter the next trade after writing the setup down.`,
    namedPattern: "Win-rate Collapse After Loss",
    citation: "Coval & Shumway 2005",
    research: HI
      ? `Loss ke baad WR girna — Coval & Shumway (2005) ke according — focus aur emotional regulation reflect karta hai, sirf strategy nahi. Concrete fix: structural cool-off rule, willpower nahi.`
      : `Coval & Shumway (2005) — a WR drop after losses reflects focus / emotional regulation, not strategy quality. The concrete fix is a structural cool-off rule, not willpower.`,
    examples: postLoss.examples || null,
  };
}

function buildAfterWinCard(b, HI, pe) {
  const aw = b.sequence?.after_win || {};
  const overallWr = b.sequence?.overall_win_rate_pct ?? 0;
  if ((aw.sample ?? 0) === 0) return null;
  const overconfidence = aw.overconfidence_size_up_flag === true;
  const carelessness =
    !overconfidence && (overallWr - (aw.win_rate_pct ?? 0)) >= 10;

  const oc = pe?.overconfidence_after_wins || {};
  const triggerCount = oc.trigger_count ?? 0;
  const impactUsd = fmtImpact(oc.cost_usd);
  const evidenceCount = overconfidence && triggerCount > 0 ? triggerCount : aw.sample;

  let severity, namedPattern, citation, research, action;
  if (overconfidence) {
    severity = "high";
    namedPattern = "Overconfidence Bias";
    citation = "Barber & Odean 2000";
    research = HI
      ? `Win ke baad brain "main correct hoon" mode mein chala jaata hai. Barber & Odean (2000) ne paya tha overconfident traders annually 6.5% underperform karte hain — aksar winning streak ke baad size badhane ki wajah se.`
      : `After a win, the brain shifts into "I was right" mode. Barber & Odean (2000) found overconfident traders underperform by 6.5% annually, largely from sizing up after wins.`;
    action = HI
      ? `Agar main koi trade win mein close karu (aapki size tab ${aw.avg_position_size_vs_overall_ratio}× avg ho jaati hai), to main next trade ki position size barabar ya kam rakhunga — kabhi badi nahi.`
      : `If I close a trade in profit (size jumps to ${aw.avg_position_size_vs_overall_ratio}× average), then I will keep the next trade's size equal or smaller — never bigger.`;
  } else if (carelessness) {
    severity = "warn";
    namedPattern = "Post-win Carelessness";
    citation = null;
    research = HI
      ? `Win ke baad focus loosen ho jaata hai — yeh overconfidence se alag hai. Position size to nahi badhi, par attention drop ho gaya.`
      : `Focus loosens after a win — distinct from overconfidence. Sizing didn't increase, but attention slipped.`;
    action = HI
      ? `Agar main koi trade win mein close karu (after-win WR ${aw.win_rate_pct}% overall ${overallWr}% se kam hai), to main ek deep breath lunga, charts fresh check karunga, phir hi next entry lunga.`
      : `If I close a trade in profit (after-win WR ${aw.win_rate_pct}% is below overall ${overallWr}%), then I will take a deep breath, re-check the setup fresh, then enter the next trade.`;
  } else {
    severity = "good";
    namedPattern = "Post-win Discipline";
    citation = null;
    research = HI
      ? `Win ke baad bhi same discipline maintain karna rare strength hai — most traders yahaan slip karte hain.`
      : `Maintaining the same discipline after wins is a rare strength — most traders slip here.`;
    action = HI
      ? `Agar main koi trade win mein close karu (current size ${aw.avg_position_size_vs_overall_ratio}×, after-win WR ${aw.win_rate_pct}%), to main same discipline aur same size next trade pe bhi maintain karunga.`
      : `If I close a trade in profit (size ${aw.avg_position_size_vs_overall_ratio}×, after-win WR ${aw.win_rate_pct}%), then I will keep the same discipline and same size on the next trade.`;
  }

  return {
    testId: "bias-card-after-win",
    icon: Sparkles,
    title: HI ? "After-win Behaviour" : "After-win Behaviour",
    severity,
    evidence: `${evidenceCount} ${evidenceCount === 1 ? "trade" : "trades"}`,
    impact:
      overconfidence && impactUsd
        ? { value: impactUsd, tone: "var(--bt-loss)" }
        : null,
    action,
    namedPattern,
    citation,
    research,
    examples: overconfidence ? oc.examples : null,
  };
}

function buildRMultipleCard(b, HI, pe) {
  const r = b.r_multiple || {};
  const total = (r.sample ?? 0) + (r.excluded_no_sl ?? 0);
  if (total === 0) return null;
  const zeroSL = (r.sample ?? 0) === 0;
  const noSl = pe?.no_stop_loss_losers || {};
  const noSlCount = noSl.trigger_count ?? r.excluded_no_sl ?? 0;
  const impactUsd = fmtImpact(noSl.cost_usd);

  let severity, namedPattern, citation, research, action, evidence, impact;
  if (zeroSL) {
    severity = "high";
    namedPattern = "No-SL Discipline Gap";
    citation = null;
    research = HI
      ? `Bina pre-defined SL ke position sizing arbitrary ho jaati hai aur max loss theoretically unlimited hota hai. Yeh foundational fix hai — risk-adjusted performance literally measure hi nahi ho sakti.`
      : `Without a pre-defined SL, position sizing becomes arbitrary and max loss is theoretically unlimited. Foundational fix — risk-adjusted performance literally cannot be measured.`;
    action = HI
      ? `Agar main koi trade execute karu (saare ${r.excluded_no_sl} trades bina SL ke the), to main entry ke saath hi pre-defined stop-loss set karunga — kuch bhi baaki seekhne se pehle.`
      : `If I execute a trade (all ${r.excluded_no_sl} trades had no stop-loss), then I will set a pre-defined stop-loss at entry — before learning anything else.`;
    evidence = `${noSlCount} ${noSlCount === 1 ? "trade" : "trades"}`;
    impact = impactUsd ? { value: impactUsd, tone: "var(--bt-loss)" } : null;
  } else {
    const avgR = r.avg_r ?? 0;
    severity = avgR >= 0.5 ? "good" : (avgR >= 0 ? "warn" : "high");
    namedPattern = "R-multiple Discipline";
    citation = null;
    research = HI
      ? `R-multiple aapki risk-per-trade ka real measure deta hai. Avg R consistently 0.5+ hona long-term profitability ka strongest indicator hai.`
      : `R-multiple measures real risk-per-trade efficiency. A consistent avg-R of 0.5+ is the strongest indicator of long-term profitability.`;
    action = severity === "good"
      ? (HI
          ? `Agar main avg R ${avgR.toFixed(2)} maintain karu, to main apne worst trade (${(r.worst_r ?? 0).toFixed(2)}R) ka review karunga — kya wahaan SL mid-trade widen kiya tha?`
          : `If I keep my avg R solid at ${avgR.toFixed(2)}, then I will review my worst trade (${(r.worst_r ?? 0).toFixed(2)}R) — did the SL get widened mid-trade?`)
      : (HI
          ? `Agar main avg R ${avgR.toFixed(2)} ke neeche dekh raha hu, to main losers jaldi cut karunga aur winners ko letne dunga.`
          : `If I see my avg R drop below target (${avgR.toFixed(2)}), then I will cut losers faster and let winners run longer.`);
    evidence = `${r.sample} ${r.sample === 1 ? "trade" : "trades"}`;
    impact = severity === "high" && impactUsd ? { value: impactUsd, tone: "var(--bt-loss)" } : null;
  }

  return {
    testId: "bias-card-r-multiple",
    icon: ShieldCheck,
    title: HI ? "R-multiple / SL Discipline" : "R-multiple / SL Discipline",
    severity,
    evidence,
    impact,
    action,
    namedPattern,
    citation,
    research,
    examples: zeroSL ? noSl.examples : null,
  };
}

function buildFeeDragCard(b, HI, pe) {
  const f = b.fee_drag || {};
  if ((f.gross_win_usd ?? 0) <= 0 && (f.total_fees_usd ?? 0) === 0) return null;
  const flagged = f.high_fee_drag_flag === true;
  const hf = pe?.high_fee_trades || {};
  const evidenceCount = hf.trigger_count ?? 0;
  const impactUsd = fmtImpact(-(f.total_fees_usd ?? 0));

  return {
    testId: "bias-card-fee-drag",
    icon: Wallet,
    title: HI ? "Fee Drag" : "Fee Drag",
    severity: flagged ? "high" : "good",
    evidence:
      evidenceCount > 0
        ? `${evidenceCount} ${evidenceCount === 1 ? "trade" : "trades"}`
        : `${f.pct_of_gross_win}% of wins`,
    impact: impactUsd ? { value: impactUsd, tone: flagged ? "var(--bt-loss)" : "var(--bt-text-muted)" } : null,
    action: flagged
      ? (HI
          ? `Agar main fee drag ${f.pct_of_gross_win}% of gross wins dekhu, to main per-trade minimum target raise karunga ya lowest-edge scalp setups skip karunga.`
          : `If I see fee drag at ${f.pct_of_gross_win}% of gross wins, then I will raise my per-trade minimum target or skip the lowest-edge scalp setups.`)
      : (HI
          ? `Agar main fee ratio ${f.pct_of_gross_win}% of gross wins ke saaf range mein maintain karu, to main yahi setups repeat karunga aur fee structure ko monitor karta rahunga.`
          : `If I keep my fee ratio clean at ${f.pct_of_gross_win}% of gross wins, then I will keep repeating these setups and keep monitoring the fee structure.`),
    namedPattern: "Silent Edge Tax",
    citation: null,
    research: HI
      ? `25%+ fee-to-gross-win ratio ek silent edge tax hai. Har dollar of fees aapke edge se directly nikalta hai — compounding ke against work karta hai.`
      : `A 25%+ fee-to-gross-win ratio is a silent edge tax. Every fee dollar comes straight out of your edge and works against compounding.`,
    examples: flagged ? hf.examples : null,
  };
}

function buildSymbolSideCard(b, HI) {
  const rows = (b.symbol_side_edge || []).slice(0, 5);
  if (rows.length === 0) return null;
  // Find best and worst by net P&L for the chip rendering
  const sorted = [...rows].sort((a, b2) => (b2.net_pnl_usd || 0) - (a.net_pnl_usd || 0));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  const worstNeg = worst && worst.net_pnl_usd < 0;
  const impactUsd = worstNeg ? fmtImpact(worst.net_pnl_usd) : null;
  return {
    testId: "bias-card-symbol-side",
    icon: Activity,
    title: HI ? "Symbol × Side Edge" : "Symbol × Side Edge",
    severity: worstNeg ? "warn" : "good",
    evidence: `${rows.length} ${rows.length === 1 ? "bucket" : "buckets"}`,
    impact: impactUsd ? { value: impactUsd, tone: "var(--bt-loss)" } : null,
    action: worstNeg
      ? (HI
          ? `Agar main ${worst.symbol} ${worst.side} setup dekhu (sabse weak combo, ${worst.win_rate_pct}% WR), to main yeh combo skip karunga ya size halve karunga.`
          : `If I see a ${worst.symbol} ${worst.side} setup (weakest combo, ${worst.win_rate_pct}% WR), then I will skip it or halve my size on it.`)
      : (HI
          ? `Agar main ${best.symbol} ${best.side} setup dekhu (sabse strong combo, ${best.win_rate_pct}% WR), to main is bucket ko aur dohraunga.`
          : `If I see a ${best.symbol} ${best.side} setup (strongest combo, ${best.win_rate_pct}% WR), then I will lean into this bucket more.`),
    namedPattern: "Edge Concentration",
    citation: null,
    research: HI
      ? `Har trader ki edge specific symbol × side combos mein concentrate hoti hai. Strength ko aur dohraiye; leak ko cut karein.`
      : `Every trader's edge concentrates inside specific symbol × side buckets. Lean into strengths; cut the leaks.`,
  };
}

// ─── Exported component ──────────────────────────────────────────────────

export default function BehavioralBiasCards({ behavioral, language = "en", onExampleClick }) {
  if (!behavioral) return null;
  const HI = language === "hi";
  const pe = behavioral.pattern_evidence || {};

  const cards = [
    buildRMultipleCard(behavioral, HI, pe),    // Zero-SL takes the top slot if applicable
    buildHoldTimeCard(behavioral, HI, pe),
    buildAfterLossCard(behavioral, HI, pe),
    // Iter 99 — Post-Loss WR Collapse is a separate card (different
    // basis from After-loss Behaviour — see PDF iter-96 split).
    buildPostLossWrCollapseCard(behavioral, HI, pe),
    buildAfterWinCard(behavioral, HI, pe),
    buildFeeDragCard(behavioral, HI, pe),
    buildSymbolSideCard(behavioral, HI),
  ].filter(Boolean);

  if (cards.length === 0) return null;

  // Sort high-severity cards first so the eye lands on the riskiest pattern.
  const order = { high: 0, warn: 1, good: 2 };
  cards.sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9));

  return (
    <section
      data-testid="behavioral-bias-cards"
      className="space-y-3"
      aria-label={HI ? "Behavioural patterns detected" : "Behavioural patterns detected"}
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[var(--bt-text-muted)]" />
            {HI ? "Behavioural Patterns" : "Behavioural Patterns"}
          </h3>
          <p className="text-xs mt-0.5" style={{ color: "var(--bt-text-muted)" }}>
            {HI
              ? "Aapke trades se nikle signals — citations sirf ⓘ chip click karne pe expand honge."
              : "Signals computed from your trades — tap the ⓘ chip on a card to view the academic citation."}
          </p>
        </div>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cards.map((c) => (
          <BiasCard key={c.testId} {...c} onExampleClick={onExampleClick} />
        ))}
      </div>
    </section>
  );
}
